import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";

import {
    Card,
    CardContent,
    Grid,
    FormControl,
    InputLabel,
    MenuItem,
    Chip,
    Box,
    Select,
    TextField,
    CircularProgress,
    Tooltip as MuiTooltip,
    Autocomplete,
    Button,
    Divider,
    Checkbox,
    Stack,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CrudService from "services/cruds-service";
import DataTable from "examples/Tables/DataTable";
import { useNavigate, Link as RouterLink } from "react-router-dom";

import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend);

/** -----------------------------
 * Helpers
 * ------------------------------ */

const fetchExchangeRates = async () => {
    try {
        const res = await fetch("https://api.exchangerate-api.com/v4/latest/USD");
        return await res.json();
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

function formatDateDDMMYYYY(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
}

// ISO week helpers
function getWeekOfYear(date) {
    const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = (tempDate.getUTCDay() + 6) % 7;
    tempDate.setUTCDate(tempDate.getUTCDate() - dayNum + 3);
    const firstThursday = tempDate.valueOf();
    tempDate.setUTCMonth(0, 1);
    if (tempDate.getUTCDay() !== 4) {
        tempDate.setUTCMonth(0, 1 + ((4 - tempDate.getUTCDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - tempDate.valueOf()) / 604800000);
}

function getWeekRangeOfMonth(year, month) {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startWeek = getWeekOfYear(firstDay);
    const endWeek = getWeekOfYear(lastDay);
    return { startWeek, endWeek };
}

function buildWeeklyMonthlyYearlyCounts(opps) {
    const now = new Date();

    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const endOfYear = new Date(now.getFullYear(), 11, 31);

    let weekCount = 0,
        monthCount = 0,
        yearCount = 0;

    opps.forEach((op) => {
        const dt = new Date(op.estimatedCloseDate);
        if (isNaN(dt.getTime())) return;
        if (dt >= startOfWeek && dt <= endOfWeek) weekCount++;
        if (dt >= startOfMonth && dt <= endOfMonth) monthCount++;
        if (dt >= startOfYear && dt <= endOfYear) yearCount++;
    });

    return { weekCount, monthCount, yearCount };
}

function buildSalesRepChart(opps, startDate = null, endDate = null, selectedRep = "All") {
    const salesRepCount = {};
    const start = startDate ? new Date(startDate.startOf("day").toISOString()) : null;
    const end = endDate ? new Date(endDate.endOf("day").toISOString()) : null;

    opps.forEach((op) => {
        const dt = new Date(op.estimatedCloseDate);
        if (isNaN(dt.getTime())) return;

        if (start && end && (dt < start || dt > end)) return;

        if (selectedRep !== "All") {
            const hasMatch = Array.isArray(op.salesRep)
                ? op.salesRep.some((r) => r?.name === selectedRep)
                : (op.salesRep?.name || op.salesRep) === selectedRep;
            if (!hasMatch) return;
        }

        const rep = op.salesRep;
        if (Array.isArray(rep)) {
            rep.forEach((r) => {
                const name = r?.name || "Unknown";
                salesRepCount[name] = (salesRepCount[name] || 0) + 1;
            });
        } else if (typeof rep === "object") {
            const name = rep?.name || "Unknown";
            salesRepCount[name] = (salesRepCount[name] || 0) + 1;
        } else if (typeof rep === "string") {
            salesRepCount[rep] = (salesRepCount[rep] || 0) + 1;
        } else {
            salesRepCount["Unknown"] = (salesRepCount["Unknown"] || 0) + 1;
        }
    });

    const labels = Object.keys(salesRepCount).map((x) => (x.length > 18 ? `${x.slice(0, 16)}…` : x));

    return {
        labels,
        datasets: [
            {
                label: "Opportunity Count",
                data: Object.values(salesRepCount),
                backgroundColor: "rgba(54,162,235,0.65)",
                borderColor: "rgba(54,162,235,1)",
                borderWidth: 1,
                borderRadius: 8,
            },
        ],
    };
}

function buildLineChartData(opps, timeType, filterYear, filterMonth, rep, metric, convertCurrencyFn, currencyLabel, targetCurrency) {
    const isGlobal = timeType === "All";

    const filtered =
        rep === "All"
            ? opps
            : opps.filter((op) =>
                Array.isArray(op.salesRep) ? op.salesRep.some((r) => r?.name === rep) : (op.salesRep?.name || op.salesRep) === rep
            );

    const groups = {};

    filtered.forEach((op) => {
        const dt = new Date(op.estimatedCloseDate);
        if (isNaN(dt.getTime())) return;

        let groupKey;

        if (isGlobal || timeType === "Year") {
            groupKey = dt.getFullYear();
        } else if (timeType === "Month") {
            if (dt.getFullYear() !== filterYear) return;
            groupKey = dt.getMonth() + 1;
        } else if (timeType === "Week") {
            if (dt.getFullYear() !== filterYear) return;
            if (dt.getMonth() + 1 !== filterMonth) return;
            groupKey = getWeekOfYear(dt);
        } else {
            return;
        }

        if (!groups[groupKey]) groups[groupKey] = { count: 0, totalRevenue: 0 };

        groups[groupKey].count += 1;
        groups[groupKey].totalRevenue += convertCurrencyFn(
            Number(op.totalRevenue || 0),
            op.freightRateCurrency || currencyLabel || "USD"
        );
    });

    let period = [];
    if (isGlobal || timeType === "Year") {
        period = [2021, 2022, 2023, 2024, 2025, 2026];
    } else if (timeType === "Month") {
        period = Array.from({ length: 12 }, (_, i) => i + 1);
    } else if (timeType === "Week") {
        const { startWeek, endWeek } = getWeekRangeOfMonth(filterYear, filterMonth);
        period = Array.from({ length: endWeek - startWeek + 1 }, (_, i) => startWeek + i);
    }

    const labels = period.map((p) => {
        if (isGlobal || timeType === "Year") return String(p);
        if (timeType === "Month") return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][p - 1];
        return `W${p}`;
    });

    const dataPoints = period.map((p) => (metric === "TotalRevenue" ? groups[p]?.totalRevenue || 0 : groups[p]?.count || 0));

    return {
        labels,
        datasets: [
            {
                label: metric === "TotalRevenue" ? `Total Revenue (${targetCurrency})` : "Opportunity Count",
                data: dataPoints,
                borderColor: "rgba(75,192,192,1)",
                backgroundColor: "rgba(75,192,192,0.18)",
                fill: true,
                tension: 0.25,
                pointRadius: 3,
                pointHoverRadius: 6,
                borderWidth: 2,
            },
        ],
    };
}

/** -----------------------------
 * UI Components
 * ------------------------------ */

function StatCard({ title, value }) {
    return (
        <Card sx={{ borderRadius: "10px", boxShadow: "none", height: "100%" }}>
            <CardContent sx={{ p: 2.25 }}>
                <MDTypography variant="button" color="text" sx={{ opacity: 0.65, fontWeight: 700 }}>
                    {title}
                </MDTypography>
                <MDTypography variant="h4" sx={{ fontWeight: 700, mt: 0.25 }}>
                    {value}
                </MDTypography>
            </CardContent>
        </Card>
    );
}

function DashboardStats({ timeCounts, totalRevenue, targetCurrency, isRangeActive, totalOppCount }) {
    return (
        <Grid container spacing={2} mb={2}>
            {!isRangeActive && (
                <>
                    <Grid item xs={12} sm={4} md={3}>
                        <StatCard title="This Week" value={timeCounts.weekCount} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <StatCard title="This Month" value={timeCounts.monthCount} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <StatCard title="This Year" value={timeCounts.yearCount} />
                    </Grid>
                </>
            )}

            <Grid item xs={12} sm={isRangeActive ? 6 : 3}>
                <StatCard
                    title={isRangeActive ? "Total Opportunities (Range)" : "Total Revenue"}
                    value={
                        isRangeActive
                            ? totalOppCount
                            : `${Number(totalRevenue).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ${targetCurrency}`
                    }
                />
            </Grid>

            {isRangeActive && (
                <Grid item xs={12} sm={6}>
                    <StatCard
                        title="Total Revenue (Range)"
                        value={`${Number(totalRevenue).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ${targetCurrency}`}
                    />
                </Grid>
            )}
        </Grid>
    );
}

const PaperCard = ({ title, action, children, sx }) => (
    <Card
        sx={{
            borderRadius: "10px",
            boxShadow: "none",
            overflow: "hidden",
            height: "100%",
            ...sx,
        }}
    >
        {(title || action) && (
            <Box
                sx={{
                    px: 2.25,
                    py: 1.5,
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    bgcolor: "rgba(0,0,0,0.02)",
                }}
            >
                <MDTypography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                    {title}
                </MDTypography>
                {action ? <Box>{action}</Box> : null}
            </Box>
        )}
        <CardContent sx={{ p: 2.5 }}>{children}</CardContent>
    </Card>
);

/** -----------------------------
 * Chart Options
 * ------------------------------ */

const commonChartLayout = {
    padding: { left: 10, right: 12, top: 8, bottom: 10 },
};

const commonLegend = {
    labels: {
        boxWidth: 10,
        boxHeight: 10,
        padding: 12,
        usePointStyle: true,
    },
};

const makeBarOptions = (labelCount) => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: commonChartLayout,
    plugins: {
        legend: { display: false },
        tooltip: { intersect: false },
    },
    scales: {
        x: {
            offset: true,
            grid: { display: false, drawBorder: true },
            ticks: {
                display: true,
                autoSkip: labelCount > 10,
                maxTicksLimit: 10,
                maxRotation: 0,
                minRotation: 0,
                padding: 8,
                font: { size: 11 },
            },
        },
        y: {
            beginAtZero: true,
            grid: { color: "rgba(0,0,0,0.06)", drawBorder: true },
            ticks: { stepSize: 1, padding: 8, font: { size: 11 } },
        },
    },
});

const makeDoughnutOptions = (onClickFn) => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: commonChartLayout,
    cutout: "62%",
    plugins: {
        legend: { position: "bottom", ...commonLegend },
        tooltip: { intersect: false },
    },
    onClick: onClickFn,
});

const makeLineOptions = (selectedMetric, targetCurrency) => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: commonChartLayout,
    plugins: {
        legend: { display: false },
        tooltip: {
            intersect: false,
            callbacks: {
                label: (context) => {
                    if (selectedMetric === "TotalRevenue") return `${context.dataset.label}: ${context.formattedValue} ${targetCurrency}`;
                    return `${context.dataset.label}: ${context.formattedValue}`;
                },
            },
        },
    },
    scales: {
        x: {
            grid: { display: false, drawBorder: true },
            ticks: {
                display: true,
                autoSkip: true,
                maxTicksLimit: 12,
                maxRotation: 0,
                minRotation: 0,
                padding: 8,
                font: { size: 11 },
            },
        },
        y: {
            beginAtZero: true,
            ticks: { padding: 8, stepSize: selectedMetric === "TotalRevenue" ? undefined : 1, font: { size: 11 } },
            grid: { color: "rgba(0,0,0,0.06)", drawBorder: true },
        },
    },
});

/** -----------------------------
 * Main
 * ------------------------------ */

function FullIntegratedDashboard() {
    const [opportunities, setOpportunities] = useState([]);
    const [timeCounts, setTimeCounts] = useState({ weekCount: 0, monthCount: 0, yearCount: 0 });
    const [loading, setLoading] = useState(true);

    const [exchangeRates, setExchangeRates] = useState(null);
    const [targetCurrency, setTargetCurrency] = useState("USD");

    const navigate = useNavigate();

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [selectedMeetingStatus, setSelectedMeetingStatus] = useState(null);

    const [timeType, setTimeType] = useState("Month");
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedRep, setSelectedRep] = useState("All");
    const [selectedMetric, setSelectedMetric] = useState("OpportunityCount");
    const [lineChartData, setLineChartData] = useState(null);

    const [meetings, setMeetings] = useState([]);
    const [meetingStatusData, setMeetingStatusData] = useState(null);

    const [opportunityStatusData, setOpportunityStatusData] = useState(null);
    const [selectedOpportunityStatus, setSelectedOpportunityStatus] = useState(null);

    const ALLOWED_OPP_STATUS_LABELS = ["Prospect", "Needs Assessment", "Target"];
    const [selectedOppStatuses, setSelectedOppStatuses] = useState(ALLOWED_OPP_STATUS_LABELS);

    const [filteredOpps, setFilteredOpps] = useState([]);

    const [salesRepList, setSalesRepList] = useState([]);
    const [salesRepBarData, setSalesRepBarData] = useState(null);

    const [totalProfit] = useState({
        totalCount: 0,
        sumPerTruck: 0,
        sumPercentage: 0,
        currencyLabel: "",
    });

    const normalizeOppStatus = useCallback((op) => {
        const raw = op?.status ?? op?.opportunityStatus;
        if (!raw) return "";
        if (typeof raw === "string") return raw.trim();
        if (typeof raw === "object" && raw.label) return String(raw.label).trim();
        return String(raw).trim();
    }, []);

    const applyOppStatusFilter = useCallback(
        (data) => {
            if (!selectedOppStatuses || selectedOppStatuses.length === 0) return [];
            const set = new Set(selectedOppStatuses);
            return data.filter((op) => set.has(normalizeOppStatus(op)));
        },
        [normalizeOppStatus, selectedOppStatuses]
    );

    const convertCurrency = useCallback(
        (amount, fromCurrency) => {
            if (!exchangeRates || !exchangeRates.rates) return amount;
            const baseRate = exchangeRates.rates[fromCurrency];
            const targetRate = exchangeRates.rates[targetCurrency];
            if (!baseRate || !targetRate) return amount;
            return (amount / baseRate) * targetRate;
        },
        [exchangeRates, targetCurrency]
    );

    const activeOpportunities = useMemo(() => {
        let filtered = opportunities;

        if (startDate && endDate) {
            const start = new Date(startDate.startOf("day").toISOString());
            const end = new Date(endDate.endOf("day").toISOString());
            filtered = filtered.filter((op) => {
                const dt = new Date(op.estimatedCloseDate);
                if (isNaN(dt.getTime())) return false;
                return dt >= start && dt <= end;
            });
        }

        if (selectedRep !== "All") {
            filtered = filtered.filter((op) =>
                Array.isArray(op.salesRep) ? op.salesRep.some((r) => r?.name === selectedRep) : (op.salesRep?.name || op.salesRep) === selectedRep
            );
        }

        filtered = applyOppStatusFilter(filtered);
        return filtered;
    }, [opportunities, startDate, endDate, selectedRep, applyOppStatusFilter]);

    useEffect(() => {
        setTimeCounts(buildWeeklyMonthlyYearlyCounts(activeOpportunities));
    }, [activeOpportunities]);

    const totalRevenue = useMemo(() => {
        return activeOpportunities
            .reduce((total, op) => {
                const amount = Number(op.totalRevenue || 0);
                const fromCurrency = op.freightRateCurrency || "USD";
                const baseRate = exchangeRates?.rates?.[fromCurrency];
                const targetRate = exchangeRates?.rates?.[targetCurrency];
                if (!baseRate || !targetRate) return total;
                return total + (amount / baseRate) * targetRate;
            }, 0)
            .toFixed(2);
    }, [activeOpportunities, exchangeRates, targetCurrency]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const resp = await CrudService.getOpportunities();
                const data = resp.data.map((item) => ({ id: item.id, ...item.attributes }));
                setOpportunities(data);

                const meetingResp = await CrudService.getMeetings();
                const meetingList = meetingResp.data.map((item) => ({ id: item.id, ...item.attributes }));
                setMeetings(meetingList);

                const rates = await fetchExchangeRates();
                setExchangeRates(rates);

                const repNamesSet = new Set();
                data.forEach((op) => {
                    if (Array.isArray(op.salesRep)) op.salesRep.forEach((r) => r?.name && repNamesSet.add(r.name));
                    else if (op.salesRep?.name || typeof op.salesRep === "string") repNamesSet.add(op.salesRep?.name || op.salesRep);
                });
                meetingList.forEach((m) => {
                    if (Array.isArray(m.salesRep)) m.salesRep.forEach((r) => r?.name && repNamesSet.add(r.name));
                    else if (m.salesRep?.name || typeof m.salesRep === "string") repNamesSet.add(m.salesRep?.name || m.salesRep);
                });
                setSalesRepList(Array.from(repNamesSet));
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    useEffect(() => {
        setSalesRepBarData(buildSalesRepChart(opportunities, startDate, endDate, selectedRep));
    }, [opportunities, startDate, endDate, selectedRep]);

    useEffect(() => {
        const statusCount = {};
        meetings.forEach((m) => {
            const dt = new Date(m.meetingDate);
            if (isNaN(dt.getTime())) return;

            if (startDate && endDate) {
                const start = new Date(startDate.startOf("day").toISOString());
                const end = new Date(endDate.endOf("day").toISOString());
                if (dt < start || dt > end) return;
            }

            if (selectedRep !== "All") {
                if (Array.isArray(m.salesRep)) {
                    if (!m.salesRep.some((r) => r?.name === selectedRep)) return;
                } else if ((m.salesRep?.name || m.salesRep) !== selectedRep) return;
            }

            const status = m.meetingStatus?.trim() || "Unknown";
            statusCount[status] = (statusCount[status] || 0) + 1;
        });

        setMeetingStatusData({
            labels: Object.keys(statusCount),
            datasets: [
                {
                    label: "Meetings",
                    data: Object.values(statusCount),
                    backgroundColor: [
                        "rgba(54,162,235,0.65)",
                        "rgba(255,99,132,0.65)",
                        "rgba(255,206,86,0.65)",
                        "rgba(75,192,192,0.65)",
                        "rgba(153,102,255,0.65)",
                        "rgba(255,159,64,0.65)",
                    ],
                    borderWidth: 1,
                },
            ],
        });
    }, [meetings, startDate, endDate, selectedRep]);

    useEffect(() => {
        const statusCountOpp = {};
        opportunities.forEach((op) => {
            const dt = new Date(op.estimatedCloseDate);
            if (isNaN(dt.getTime())) return;

            if (startDate && endDate) {
                const start = new Date(startDate.startOf("day").toISOString());
                const end = new Date(endDate.endOf("day").toISOString());
                if (dt < start || dt > end) return;
            }

            if (selectedRep !== "All") {
                if (Array.isArray(op.salesRep)) {
                    if (!op.salesRep.some((r) => r?.name === selectedRep)) return;
                } else if ((op.salesRep?.name || op.salesRep) !== selectedRep) return;
            }

            const status = normalizeOppStatus(op) || "Unknown";
            statusCountOpp[status] = (statusCountOpp[status] || 0) + 1;
        });

        setOpportunityStatusData({
            labels: Object.keys(statusCountOpp),
            datasets: [
                {
                    label: "Opportunities",
                    data: Object.values(statusCountOpp),
                    backgroundColor: [
                        "rgba(54,162,235,0.65)",
                        "rgba(255,99,132,0.65)",
                        "rgba(255,206,86,0.65)",
                        "rgba(75,192,192,0.65)",
                        "rgba(153,102,255,0.65)",
                        "rgba(255,159,64,0.65)",
                    ],
                    borderWidth: 1,
                },
            ],
        });
    }, [opportunities, startDate, endDate, selectedRep, selectedOppStatuses, normalizeOppStatus]);

    useEffect(() => {
        if (selectedRep === "All") {
            setSelectedOpportunityStatus(null);
            setSelectedMeetingStatus(null);
        }
    }, [selectedRep]);

    useEffect(() => {
        if (!exchangeRates?.rates) return;

        const safeYear = selectedYear ?? new Date().getFullYear();
        const safeMonth = selectedMonth ?? new Date().getMonth() + 1;

        setLineChartData(
            buildLineChartData(opportunities, timeType, safeYear, safeMonth, selectedRep, selectedMetric, convertCurrency, totalProfit.currencyLabel, targetCurrency)
        );

        let newFiltered = opportunities.filter((op) => {
            const dt = new Date(op.estimatedCloseDate);
            if (isNaN(dt.getTime())) return false;

            if (
                selectedRep !== "All" &&
                !(
                    Array.isArray(op.salesRep)
                        ? op.salesRep.some((r) => r?.name === selectedRep)
                        : (op.salesRep?.name || op.salesRep) === selectedRep
                )
            )
                return false;

            if (timeType === "Week") {
                if (dt.getFullYear() !== safeYear) return false;
                if (dt.getMonth() + 1 !== safeMonth) return false;
            } else if (timeType === "Month") {
                if (dt.getFullYear() !== safeYear) return false;
            }

            return true;
        });

        newFiltered = applyOppStatusFilter(newFiltered);
        setFilteredOpps(newFiltered);
    }, [
        opportunities,
        exchangeRates,
        targetCurrency,
        selectedRep,
        timeType,
        selectedYear,
        selectedMonth,
        selectedMetric,
        convertCurrency,
        applyOppStatusFilter,
        totalProfit.currencyLabel,
    ]);

    const handleTimeTypeChange = (e) => {
        const val = e.target.value;
        setTimeType(val);
        if (val === "All") {
            setSelectedYear(null);
            setSelectedMonth(null);
        } else if (!selectedYear) {
            setSelectedYear(new Date().getFullYear());
            setSelectedMonth(new Date().getMonth() + 1);
        }
    };

    const handleYearChange = (e) => setSelectedYear(Number(e.target.value));
    const handleMonthChange = (e) => setSelectedMonth(Number(e.target.value));
    const handleRepChange = (e) => setSelectedRep(e.target.value);
    const handleMetricChange = (e) => setSelectedMetric(e.target.value);

    const handleMeetingDoughnutClick = (event, elements) => {
        if (!elements?.length || !meetingStatusData) return;
        const idx = elements[0].index;
        const status = meetingStatusData.labels[idx];
        setSelectedMeetingStatus((prev) => (prev === status ? null : status));
    };

    const handleOpportunityDoughnutClick = (event, elements) => {
        if (!elements?.length || !opportunityStatusData) return;
        const idx = elements[0].index;
        const status = opportunityStatusData.labels[idx];
        setSelectedOpportunityStatus((prev) => (prev === status ? null : status));
    };

    const isDefaultOppStatuses =
        selectedOppStatuses?.length === ALLOWED_OPP_STATUS_LABELS.length && ALLOWED_OPP_STATUS_LABELS.every((s) => selectedOppStatuses.includes(s));

    const isClearEnabled =
        !!(startDate && endDate) ||
        selectedRep !== "All" ||
        !!selectedMeetingStatus ||
        !!selectedOpportunityStatus ||
        !isDefaultOppStatuses ||
        timeType !== "Month" ||
        selectedYear !== new Date().getFullYear() ||
        selectedMonth !== new Date().getMonth() + 1 ||
        selectedMetric !== "OpportunityCount" ||
        filteredOpps.length > 0;

    const clearAll = () => {
        setStartDate(null);
        setEndDate(null);
        setSelectedRep("All");
        setSelectedOppStatuses(ALLOWED_OPP_STATUS_LABELS);
        setSelectedMeetingStatus(null);
        setSelectedOpportunityStatus(null);

        setTimeType("Month");
        setSelectedYear(new Date().getFullYear());
        setSelectedMonth(new Date().getMonth() + 1);
        setSelectedMetric("OpportunityCount");

        setFilteredOpps([]);
    };

    const filteredColumns = [
        { Header: "Customer", accessor: "customer" },
        { Header: "Opp Name", accessor: "opportunityName" },
        { Header: "Estimated Close", accessor: "estimatedCloseDate" },
        { Header: "Transport Type", accessor: "transportType" },
        { Header: `Total Revenue (${targetCurrency})`, accessor: "totalRevenue" },
        { Header: "Sales Rep", accessor: "salesRep" },
        { Header: "Status", accessor: "status" },
    ];

    const filteredRows = filteredOpps.map((op) => {
        const revenue = convertCurrency(Number(op.totalRevenue || 0), op.freightRateCurrency || "USD");
        return {
            customer: (
                <MuiTooltip title={op.customer || ""} placement="top" arrow>
                    <span>{op.customer?.length > 40 ? `${op.customer.slice(0, 37)}...` : op.customer || ""}</span>
                </MuiTooltip>
            ),
            opportunityName: (
                <span
                    style={{ color: "#ff7400", cursor: "pointer", textDecoration: "underline", fontWeight: 700 }}
                    onClick={() => navigate(`/opportunity/create-opportunity/details?id=${op.id}`)}
                >
          {op.opportunityName || "-"}
        </span>
            ),
            estimatedCloseDate: formatDateDDMMYYYY(op.estimatedCloseDate),
            transportType: op.selectedTransportType || "-",
            totalRevenue: `${revenue.toFixed(2)} ${targetCurrency}`,
            salesRep: Array.isArray(op.salesRep) ? op.salesRep.map((rep) => rep.name).join(", ") : op.salesRep?.name || op.salesRep || "-",
            status: normalizeOppStatus(op) || "-",
        };
    });

    const totalRevenueSum = filteredOpps.reduce((sum, op) => {
        const revenue = convertCurrency(Number(op.totalRevenue || 0), op.freightRateCurrency || "USD");
        return sum + revenue;
    }, 0);

    if (filteredRows.length) {
        filteredRows.push({
            customer: <strong>Total</strong>,
            opportunityName: "",
            estimatedCloseDate: "",
            transportType: "",
            totalRevenue: <strong>{totalRevenueSum.toFixed(2)} {targetCurrency}</strong>,
            salesRep: "",
            status: "",
        });
    }

    const showFilteredTable = filteredOpps.length > 0;

    if (loading) {
        return (
            <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="420px">
                <CircularProgress />
            </MDBox>
        );
    }

    const barOptions = makeBarOptions(salesRepBarData?.labels?.length || 0);
    const doughnutMeetingsOptions = makeDoughnutOptions(handleMeetingDoughnutClick);
    const doughnutOppOptions = makeDoughnutOptions(handleOpportunityDoughnutClick);
    const lineOptions = makeLineOptions(selectedMetric, targetCurrency);

    return (
        <MDBox pb={3}>
            {/* HEADER */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <Box>
                    <MDTypography variant="h4" fontWeight="bold">
                        Sales Dashboard
                    </MDTypography>
                </Box>

                <Button
                    variant="contained"
                    onClick={clearAll}
                    disabled={!isClearEnabled}
                    sx={{ backgroundColor: "#FFFFFF", color: "#101828 !important", border: "1px solid rgba(15, 23, 42, 0.12)", borderRadius: "8px", textTransform: "none", fontWeight: 600, boxShadow: "none", "&:hover": { backgroundColor: "#FFFFFF", borderColor: "#175CD3", color: "#175CD3 !important", boxShadow: "none" } }}
                >
                    Clear
                </Button>
            </Box>

            {/* FILTERS */}
            <Card sx={{ borderRadius: "10px", mb: 2, boxShadow: "none" }}>
                <CardContent sx={{ p: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                            <TextField
                                select
                                label="Currency"
                                value={targetCurrency}
                                onChange={(e) => setTargetCurrency(e.target.value)}
                                SelectProps={{ native: true }}
                                sx={{ minWidth: 140, "& .MuiInputBase-root": { height: 40 } }}
                                size="small"
                            >
                                {["USD", "TRY", "EUR", "GBP"].map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </TextField>

                            <DatePicker
                                label="Start"
                                format="DD.MM.YYYY"
                                value={startDate}
                                onChange={(v) => setStartDate(v)}
                                slotProps={{
                                    textField: { variant: "outlined", size: "small", sx: { minWidth: 160, "& .MuiInputBase-root": { height: 40 } } },
                                }}
                            />

                            <DatePicker
                                label="End"
                                format="DD.MM.YYYY"
                                value={endDate}
                                onChange={(v) => setEndDate(v)}
                                slotProps={{
                                    textField: { variant: "outlined", size: "small", sx: { minWidth: 160, "& .MuiInputBase-root": { height: 40 } } },
                                }}
                            />

                            <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Sales Rep</InputLabel>
                                <Select label="Sales Rep" value={selectedRep} onChange={handleRepChange} sx={{ height: 40 }}>
                                    <MenuItem value="All">All</MenuItem>
                                    {salesRepList.map((rep, idx) => {
                                        const label = typeof rep === "object" ? rep.name : rep;
                                        const value = typeof rep === "object" ? rep.name : rep;
                                        return (
                                            <MenuItem key={idx} value={value}>
                                                {label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>

                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={ALLOWED_OPP_STATUS_LABELS}
                                value={selectedOppStatuses}
                                onChange={(_, nv) => setSelectedOppStatuses(nv)}
                                renderOption={({ key: optionKey, ...props }, option, { selected }) => (
                                    <li key={optionKey} {...props}>
                                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                                        {option}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} label="Opp Status" size="small" variant="outlined" sx={{ minWidth: 240 }} />
                                )}
                            />
                        </Box>
                    </LocalizationProvider>

                    <Box mt={1.5} display="flex" gap={1} flexWrap="wrap" alignItems="center">
                        <Chip size="small" label={`Currency: ${targetCurrency}`} sx={{ fontWeight: 700, borderRadius: 2 }} />
                        {startDate && endDate ? (
                            <Chip size="small" label={`Date: ${startDate.format("DD.MM.YYYY")} - ${endDate.format("DD.MM.YYYY")}`} sx={{ fontWeight: 700, borderRadius: 2 }} />
                        ) : (
                            <Chip size="small" label="Date: All" sx={{ fontWeight: 700, borderRadius: 2, opacity: 0.7 }} />
                        )}
                        <Chip
                            size="small"
                            label={selectedRep !== "All" ? `SalesRep: ${selectedRep}` : "SalesRep: All"}
                            sx={{ fontWeight: 700, borderRadius: 2, opacity: selectedRep !== "All" ? 1 : 0.7 }}
                        />
                        <Chip size="small" label={`Opp Status: ${selectedOppStatuses?.length || 0}`} sx={{ fontWeight: 700, borderRadius: 2 }} />
                        {selectedMeetingStatus ? (
                            <Chip size="small" color="success" label={`Meeting: ${selectedMeetingStatus}`} onDelete={() => setSelectedMeetingStatus(null)} sx={{ fontWeight: 700, borderRadius: 2 }} />
                        ) : null}
                        {selectedOpportunityStatus ? (
                            <Chip size="small" color="success" label={`Opp: ${selectedOpportunityStatus}`} onDelete={() => setSelectedOpportunityStatus(null)} sx={{ fontWeight: 700, borderRadius: 2 }} />
                        ) : null}
                    </Box>
                </CardContent>
            </Card>

            {/* STATS */}
            <DashboardStats
                timeCounts={timeCounts}
                totalRevenue={totalRevenue}
                targetCurrency={targetCurrency}
                isRangeActive={!!(startDate && endDate)}
                totalOppCount={activeOpportunities.length}
            />

            {/* CHARTS */}
            <Grid container spacing={2} mb={2} alignItems="stretch">
                <Grid item xs={12} lg={6}>
                    <PaperCard title="Opportunities by Sales Rep" sx={{ minHeight: 480 }}>
                        <Box sx={{ height: 390 }}>{salesRepBarData ? <Bar data={salesRepBarData} options={barOptions} /> : null}</Box>
                    </PaperCard>
                </Grid>

                <Grid item xs={12} lg={6}>
                    <PaperCard
                        title="Meeting Status"
                        action={
                            selectedMeetingStatus ? (
                                <Button size="small" variant="outlined" onClick={() => setSelectedMeetingStatus(null)} sx={{ textTransform: "none" }}>
                                    Clear
                                </Button>
                            ) : null
                        }
                        sx={{ minHeight: 480 }}
                    >
                        <Box sx={{ height: 390 }}>{meetingStatusData ? <Doughnut data={meetingStatusData} options={doughnutMeetingsOptions} /> : null}</Box>
                    </PaperCard>
                </Grid>

                <Grid item xs={12} lg={6}>
                    <PaperCard
                        title="Opportunity Status"
                        action={
                            selectedOpportunityStatus ? (
                                <Button size="small" variant="outlined" onClick={() => setSelectedOpportunityStatus(null)} sx={{ textTransform: "none" }}>
                                    Clear
                                </Button>
                            ) : null
                        }
                        sx={{ minHeight: 480 }}
                    >
                        <Box sx={{ height: 390 }}>{opportunityStatusData ? <Doughnut data={opportunityStatusData} options={doughnutOppOptions} /> : null}</Box>
                    </PaperCard>
                </Grid>

                {/* TREND: Controls directly ABOVE the chart (same card) */}
                <Grid item xs={12} lg={6}>
                    <PaperCard title="Trend" sx={{ minHeight: 480 }}>
                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Time</InputLabel>
                                <Select label="Time" value={timeType} onChange={handleTimeTypeChange}>
                                    <MenuItem value="Week">Week</MenuItem>
                                    <MenuItem value="Month">Month</MenuItem>
                                    <MenuItem value="Year">Year</MenuItem>
                                    <MenuItem value="All">All</MenuItem>
                                </Select>
                            </FormControl>

                            {timeType !== "All" && timeType !== "Year" && (
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Year</InputLabel>
                                    <Select label="Year" value={selectedYear ?? new Date().getFullYear()} onChange={handleYearChange}>
                                        {[2021, 2022, 2023, 2024, 2025, 2026].map((y) => (
                                            <MenuItem key={y} value={y}>
                                                {y}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {timeType === "Week" && (
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Month</InputLabel>
                                    <Select label="Month" value={selectedMonth ?? new Date().getMonth() + 1} onChange={handleMonthChange}>
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                            <MenuItem key={m} value={m}>
                                                {m}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                <InputLabel>Metric</InputLabel>
                                <Select label="Metric" value={selectedMetric} onChange={handleMetricChange}>
                                    <MenuItem value="OpportunityCount">Opportunity Count</MenuItem>
                                    <MenuItem value="TotalRevenue">Total Revenue</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ height: 330 }}>
                            {lineChartData ? <Line data={lineChartData} options={lineOptions} /> : null}
                        </Box>
                    </PaperCard>
                </Grid>
            </Grid>

            {/* SELECTED OPP STATUS TABLE */}
            {selectedOpportunityStatus && (
                <PaperCard title={`Opportunities: ${selectedOpportunityStatus}`} sx={{ mb: 2 }}>
                    <DataTable
                        table={{
                            columns: [
                                { Header: "Customer", accessor: "customer" },
                                { Header: "Opp Name", accessor: "opportunityName" },
                                { Header: "Estimated Close", accessor: "estimatedCloseDate" },
                                { Header: "Transport Type", accessor: "transportType" },
                                { Header: `Total Revenue (${targetCurrency})`, accessor: "totalRevenue" },
                                { Header: "Sales Rep", accessor: "salesRep" },
                            ],
                            rows: (() => {
                                const filtered = opportunities.filter((op) => {
                                    const dt = new Date(op.estimatedCloseDate);
                                    if (isNaN(dt.getTime())) return false;

                                    if (startDate && endDate) {
                                        const start = new Date(startDate.startOf("day").toISOString());
                                        const end = new Date(endDate.endOf("day").toISOString());
                                        if (dt < start || dt > end) return false;
                                    }

                                    if (selectedRep !== "All") {
                                        if (Array.isArray(op.salesRep)) {
                                            if (!op.salesRep.some((r) => r?.name === selectedRep)) return false;
                                        } else if ((op.salesRep?.name || op.salesRep) !== selectedRep) return false;
                                    }

                                    return normalizeOppStatus(op) === selectedOpportunityStatus;
                                });

                                const sum = filtered.reduce((s, op) => {
                                    const rev = convertCurrency(Number(op.totalRevenue || 0), op.freightRateCurrency || "USD");
                                    return s + rev;
                                }, 0);

                                const rows = filtered.map((op) => {
                                    const revenue = convertCurrency(Number(op.totalRevenue || 0), op.freightRateCurrency || "USD");
                                    return {
                                        customer: (
                                            <MuiTooltip title={op.customer || ""} placement="top" arrow>
                                                <span>{op.customer?.length > 40 ? `${op.customer.slice(0, 37)}...` : op.customer || ""}</span>
                                            </MuiTooltip>
                                        ),
                                        opportunityName: (
                                            <span
                                                style={{ color: "#ff7400", cursor: "pointer", textDecoration: "underline", fontWeight: 700 }}
                                                onClick={() => navigate(`/opportunity/create-opportunity/details?id=${op.id}`)}
                                            >
                        {op.opportunityName || "-"}
                      </span>
                                        ),
                                        estimatedCloseDate: formatDateDDMMYYYY(op.estimatedCloseDate),
                                        transportType: op.selectedTransportType || "-",
                                        totalRevenue: `${revenue.toFixed(2)} ${targetCurrency}`,
                                        salesRep: Array.isArray(op.salesRep) ? op.salesRep.map((rep) => rep.name).join(", ") : op.salesRep?.name || op.salesRep || "-",
                                    };
                                });

                                rows.push({
                                    customer: <strong>Total</strong>,
                                    opportunityName: "",
                                    estimatedCloseDate: "",
                                    transportType: "",
                                    totalRevenue: <strong>{sum.toFixed(2)} {targetCurrency}</strong>,
                                    salesRep: "",
                                });

                                return rows;
                            })(),
                        }}
                        canSearch
                        entriesPerPage={5}
                        showTotalEntries
                        isSorted
                    />
                </PaperCard>
            )}

            {/* SELECTED MEETING STATUS TABLE */}
            {selectedMeetingStatus && (
                <PaperCard title={`Meetings: ${selectedMeetingStatus}`} sx={{ mb: 2 }}>
                    <DataTable
                        table={{
                            columns: [
                                { Header: "Title", accessor: "meetingTitle" },
                                { Header: "Status", accessor: "meetingStatus" },
                                { Header: "Date", accessor: "meetingDate" },
                                { Header: "Sales Rep", accessor: "salesRep" },
                            ],
                            rows: meetings
                                .filter((m) => {
                                    const dt = new Date(m.meetingDate);
                                    if (isNaN(dt.getTime())) return false;

                                    if (startDate && endDate) {
                                        const start = new Date(startDate.startOf("day").toISOString());
                                        const end = new Date(endDate.endOf("day").toISOString());
                                        if (dt < start || dt > end) return false;
                                    }

                                    if (selectedRep !== "All") {
                                        if (Array.isArray(m.salesRep)) {
                                            if (!m.salesRep.some((r) => r?.name === selectedRep)) return false;
                                        } else if ((m.salesRep?.name || m.salesRep) !== selectedRep) return false;
                                    }

                                    return m.meetingStatus === selectedMeetingStatus;
                                })
                                .map((m) => ({
                                    meetingTitle: (
                                        <RouterLink to={`/meeting/create-meeting/details?id=${m.id}`} style={{ textDecoration: "none" }}>
                      <span style={{ color: "#ff7400", cursor: "pointer", textDecoration: "underline", fontWeight: 700 }}>
                        {m.meetingTitle || "-"}
                      </span>
                                        </RouterLink>
                                    ),
                                    meetingStatus: m.meetingStatus || "-",
                                    meetingDate: formatDateDDMMYYYY(m.meetingDate),
                                    salesRep: Array.isArray(m.salesRep) ? m.salesRep.map((p) => p.name).join(", ") : m.salesRep || "-",
                                })),
                        }}
                        canSearch
                        entriesPerPage={5}
                        showTotalEntries
                        isSorted
                    />
                </PaperCard>
            )}

            {/* FILTERED OPPORTUNITIES TABLE */}
            {showFilteredTable && (
                <PaperCard title="Filtered Opportunities" sx={{ mb: 2 }}>
                    <DataTable table={{ columns: filteredColumns, rows: filteredRows }} canSearch entriesPerPage={5} showTotalEntries isSorted />
                </PaperCard>
            )}
        </MDBox>
    );
}

export default FullIntegratedDashboard;
